import {isNil} from 'ramda';

import type {FinancialProductMoney} from '../schemas/FinancialProductMoneySchema';
import {getCurrencyScale} from './getCurrencyScale';
import {QuoteDecimal} from './QuoteDecimal';

const PERCENT_BASE = 100;

/**
 * A percentage of the vehicle price as the backend materialises it: `roundHalfUp(P · pct / 100, S)`,
 * so a slider label equals the quote's `downPaymentAmount` / `balloonPaymentAmount`. Null when the
 * price is not a non-negative decimal amount or the percentage is not a finite number.
 */
export function calculateShareOfVehiclePrice(
  vehiclePriceWithVat: FinancialProductMoney,
  percentage: number
): FinancialProductMoney | null {
  const scale = getCurrencyScale(vehiclePriceWithVat.amount);

  if (isNil(scale) || !Number.isFinite(percentage)) {
    return null;
  }

  // P · pct / 100 is a finite decimal well inside the context precision, so the division is exact.
  const share = new QuoteDecimal(vehiclePriceWithVat.amount)
    .times(percentage)
    .dividedBy(PERCENT_BASE)
    .toDecimalPlaces(scale, QuoteDecimal.ROUND_HALF_UP);

  return {amount: share.toFixed(scale), currency: vehiclePriceWithVat.currency};
}
